import { DashboardConfigurationFormValues } from "@features/builder/DashboardConfiguration/types"
import {
  DashboardNode,
  createYamlDashboardSchema,
} from "@features/builder/DashboardConfiguration/YamlEditor/jsonSchema"
import { JSONSchema7 } from "json-schema"
import { Controller, useFormContext } from "react-hook-form"
import CodeMirror from "@uiw/react-codemirror"
import { yaml } from "@codemirror/lang-yaml"
import { EditorView, hoverTooltip } from "@codemirror/view"
import { Box } from "@mui/material"
import { Edge, useReactFlow } from "@xyflow/react"
import { AppNode, EdgeData } from "@features/builder/graph/types"
import { stateExtensions } from "@spglobal-pgm/codemirror-json-schema"
import { linter } from "@codemirror/lint"
import { yamlSchemaHover } from "@spglobal-pgm/codemirror-json-schema/yaml"
import { autocompletion } from "@codemirror/autocomplete"
import { useMemo } from "react"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { createYamlCompletionOverride } from "@features/builder/DashboardConfiguration/YamlEditor/createYamlCompletionOverride"
import { createYamlSchemaLinter } from "@features/builder/DashboardConfiguration/YamlEditor/createYamlSchemaLinter"
import { HighlightArea } from "@components/HighlightTextArea/HighlightTextArea"
import { createHighlightAreasExtension } from "@features/builder/DashboardConfiguration/YamlEditor/highlightAreasExtension"

type YamlTextAreaProps = {
  highlightedAreas?: HighlightArea[]
}

export const YamlTextArea = (props: YamlTextAreaProps) => {
  const formContext = useFormContext<DashboardConfigurationFormValues>()
  const { getNodes } = useReactFlow<AppNode, Edge<EdgeData>>()
  const nodes = getNodes()

  const mapper = nodesGroupMapper(nodes)

  const dashboardNodes = useMemo(() => {
    const typedNodes: DashboardNode[] = []

    for (const node of mapper.nodesMap.values()) {
      const label = node.data.label
      if (!label) {
        continue
      }

      typedNodes.push({
        label,
        type: node.type,
      })
    }

    return typedNodes
  }, [mapper.nodesMap])

  const yamlSchema: JSONSchema7 = useMemo(
    () => createYamlDashboardSchema(dashboardNodes),
    [dashboardNodes],
  )

  const completion = useMemo(
    () =>
      autocompletion({
        override: [createYamlCompletionOverride(Array.from(mapper.nodesMap.values()))],
      }),
    [mapper.nodesMap],
  )

  const isFieldDirty = Boolean(formContext.formState.dirtyFields["configuration"])

  // Create highlight extension based on highlightedAreas prop
  const highlightExtension = useMemo(
    () =>
      createHighlightAreasExtension({
        areas: props.highlightedAreas || [],
        disabled: isFieldDirty,
      }),
    [props.highlightedAreas, isFieldDirty],
  )

  const extensions = useMemo(
    () => [
      completion,
      yaml(),
      linter(createYamlSchemaLinter(), { autoPanel: true }),
      hoverTooltip(yamlSchemaHover()),
      stateExtensions(yamlSchema),
      EditorView.lineWrapping,
      ...highlightExtension,
    ],
    [completion, yamlSchema, highlightExtension],
  )

  return (
    <Box sx={{ height: "100%", display: "flex" }}>
      <Controller
        control={formContext.control}
        name="configuration"
        render={({ field }) => (
          <CodeMirror
            value={field.value || ""}
            onChange={field.onChange}
            extensions={extensions}
            basicSetup={{
              autocompletion: true,
              defaultKeymap: true,
              foldGutter: true,
              highlightActiveLine: false,
              highlightActiveLineGutter: true,
              highlightSelectionMatches: true,
              lineNumbers: true,
              allowMultipleSelections: true,
              syntaxHighlighting: true,
            }}
            height="100%"
            style={{ flex: 1 }}
          />
        )}
      />
    </Box>
  )
}
